import React from "react";
import { useForm } from "react-hook-form";
import { motion } from "framer-motion";
import { useBlogComments } from "../comment/useBlogComments";
import { useCreateComment } from "../comment/useCreateComment";
import { dateFormatter } from "../../helper/DateFormatter";

const CommentSection = ({ blogId }) => {
    const { register, handleSubmit, reset, formState: { errors } } = useForm();
    const { comments, isLoading } = useBlogComments(blogId);
    const { createComment, isCreating } = useCreateComment();

    const onSubmit = (data) => {
        createComment({ blogId, ...data }, {
            onSuccess: () => {
                reset();
            }
        });
    };

    return (
        <div className="container mx-auto px-4 py-16 sm:px-6 lg:px-8">
            <div className="flex items-center gap-4 mb-10">
                <div className="h-10 w-2 rounded-full bg-primary" />
                <h3 className="text-3xl font-bold text-gray-900 dark:text-white">
                    Comments <span className="text-gray-400 ml-2 text-xl font-medium">({comments?.length || 0})</span>
                </h3>
            </div>

            {/* Comment Form */}
            <div className="mb-12 overflow-hidden rounded-3xl bg-white shadow-xl ring-1 ring-black/5 dark:bg-[#1a1f2e] dark:ring-white/10">
                <div className="bg-gray-50/50 p-6 dark:bg-white/5 sm:p-8 border-b border-gray-100 dark:border-gray-800">
                    <h4 className="text-lg font-bold text-gray-900 dark:text-white">
                        Leave a comment
                    </h4>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Join the discussion and share your thoughts.
                    </p>
                </div>

                <div className="p-6 sm:p-8">
                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                        <div className="grid gap-6 sm:grid-cols-2">
                            <div className="space-y-2">
                                <label htmlFor="name" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Name
                                </label>
                                <input
                                    id="name"
                                    type="text"
                                    {...register("name", { required: "Name is required" })}
                                    className="w-full rounded-xl border-gray-200 bg-gray-50 px-4 py-3 text-gray-900 placeholder:text-gray-400 focus:border-primary focus:bg-white focus:ring-4 focus:ring-primary/10 dark:border-gray-700 dark:bg-black/20 dark:text-white dark:focus:bg-black/40 transition-all"
                                    placeholder="John Doe"
                                />
                                {errors.name && <p className="text-xs font-semibold text-red-500">{errors.name.message}</p>}
                            </div>
                            <div className="space-y-2">
                                <label htmlFor="email" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Email
                                </label>
                                <input
                                    id="email"
                                    type="email"
                                    {...register("email", {
                                        required: "Email is required",
                                        pattern: {
                                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                            message: "Invalid email address"
                                        }
                                    })}
                                    className="w-full rounded-xl border-gray-200 bg-gray-50 px-4 py-3 text-gray-900 placeholder:text-gray-400 focus:border-primary focus:bg-white focus:ring-4 focus:ring-primary/10 dark:border-gray-700 dark:bg-black/20 dark:text-white dark:focus:bg-black/40 transition-all"
                                    placeholder="john@example.com"
                                />
                                {errors.email && <p className="text-xs font-semibold text-red-500">{errors.email.message}</p>}
                            </div>
                        </div>
                        <div className="space-y-2">
                            <label htmlFor="content" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Comment
                            </label>
                            <textarea
                                id="content"
                                rows={4}
                                {...register("content", { required: "Comment cannot be empty" })}
                                className="w-full rounded-xl border-gray-200 bg-gray-50 px-4 py-3 text-gray-900 placeholder:text-gray-400 focus:border-primary focus:bg-white focus:ring-4 focus:ring-primary/10 dark:border-gray-700 dark:bg-black/20 dark:text-white dark:focus:bg-black/40 transition-all resize-none"
                                placeholder="Share your thoughts..."
                            />
                            {errors.content && <p className="text-xs font-semibold text-red-500">{errors.content.message}</p>}
                        </div>

                        <div className="flex justify-end pt-2">
                            <button
                                type="submit"
                                disabled={isCreating}
                                className="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-primary to-secondary px-8 py-3 text-sm font-bold text-white shadow-lg shadow-primary/25 transition-all hover:translate-y-[-2px] hover:shadow-xl hover:shadow-primary/30 focus:outline-none focus:ring-4 focus:ring-primary/20 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none"
                            >
                                {isCreating ? (
                                    <>
                                        <svg className="mr-2 h-4 w-4 animate-spin text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Posting...
                                    </>
                                ) : (
                                    "Post Comment"
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {/* Comment List */}
            <div className="space-y-8">
                {isLoading ? (
                    <div className="space-y-6">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="flex gap-6 animate-pulse">
                                <div className="h-12 w-12 rounded-full bg-gray-200 dark:bg-gray-800"></div>
                                <div className="flex-1 space-y-3">
                                    <div className="h-4 w-1/4 rounded bg-gray-200 dark:bg-gray-800"></div>
                                    <div className="h-20 w-full rounded-2xl bg-gray-200 dark:bg-gray-800"></div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : comments.length === 0 ? (
                    <div className="flex flex-col items-center justify-center rounded-3xl border-2 border-dashed border-gray-200 bg-gray-50/50 py-16 text-center dark:border-gray-800 dark:bg-white/5">
                        <div className="mb-4 rounded-full bg-gray-100 p-4 dark:bg-gray-800">
                            <svg className="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                            </svg>
                        </div>
                        <h4 className="text-lg font-bold text-gray-900 dark:text-white">No comments yet</h4>
                        <p className="mt-1 text-gray-500 dark:text-gray-400">Be the first to share your thoughts!</p>
                    </div>
                ) : (
                    comments.map((comment, index) => (
                        <motion.div
                            key={comment.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.1 }}
                            className="group flex gap-4 sm:gap-6"
                        >
                            <div className="flex-shrink-0">
                                <img
                                    className="h-12 w-12 rounded-full bg-gray-100 object-cover ring-2 ring-white transition-transform duration-300 group-hover:scale-110 dark:ring-gray-800"
                                    src={`https://ui-avatars.com/api/?name=${encodeURIComponent(comment.name)}&background=random`}
                                    alt={comment.name}
                                />
                            </div>
                            <div className="flex-1 space-y-2">
                                <div className="rounded-2xl rounded-tl-none bg-gray-50 p-5 dark:bg-[#1a1f2e] border border-gray-100 dark:border-gray-800">
                                    <div className="mb-2 flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <h5 className="font-bold text-gray-900 dark:text-white">
                                                {comment.name}
                                            </h5>
                                            {comment.role === 'admin' && (
                                                <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-bold text-primary">
                                                    Author
                                                </span>
                                            )}
                                        </div>
                                        <span className="text-xs font-medium text-gray-400">
                                            {dateFormatter(comment.created_at)}
                                        </span>
                                    </div>
                                    <div className="prose prose-sm max-w-none text-gray-600 dark:text-gray-300">
                                        <p>{comment.content}</p>
                                    </div>
                                </div>
                                <div className="flex gap-4 px-2">
                                    <button className="text-xs font-semibold text-gray-500 hover:text-primary dark:text-gray-400 transition-colors">
                                        Reply
                                    </button>
                                    <button className="text-xs font-semibold text-gray-500 hover:text-primary dark:text-gray-400 transition-colors">
                                        Like
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    ))
                )}
            </div>
        </div>
    );
};

export default CommentSection;
